import { IOfferHandler } from '../../types';
import { Offer } from '../../../../services/exports/Interfaces';
import { CompleteBasketItem } from '../../../../providers/BasketProvider';
import { useTranslation } from 'react-i18next';

const useFreeItemByMinSpendValue: IOfferHandler = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:Offers:OfferBadge' });

  const isUnlocked = (offer: Offer, items: CompleteBasketItem[]) =>
    items.reduce((acc, item) => acc + item.actual_price, -getProfit(offer, items)) >= offer.details.min_spend_value;
  const isEligible = (offer: Offer, items: CompleteBasketItem[]) =>
    items.some((item) => offer.details.free_products?.includes(item.entry.id));
  const getMostProfitableFreeItem = (offer: Offer, items: CompleteBasketItem[]) =>
    items
      .filter((item) => offer.details.free_products?.includes(item.entry.id))
      .reduce((acc, current) => (acc && acc.entry?.actual_price >= current.entry?.actual_price ? acc : current), null);
  const getProfit = (offer: Offer, items: CompleteBasketItem[]) =>
    getMostProfitableFreeItem(offer, items)?.entry?.actual_price ?? 0;
  const apply =
    (offer: Offer, items: CompleteBasketItem[]) =>
    (item: CompleteBasketItem): CompleteBasketItem => {
      const mostProfitableFreeItem = getMostProfitableFreeItem(offer, items);

      if (!mostProfitableFreeItem) {
        return item;
      }

      // Preview support: a preview item (menu card / product modal) carries
      // `original_index === undefined`, so the index match below cannot identify it. Fall back to
      // object identity — `getMostProfitableFreeItem` returns one of the elements of `items`, and
      // the preview is appended to that same array, so identity is exact. Comparing `entry.id`
      // instead would also match a preview whose equally-priced twin is already in the basket and
      // won the slot, wrongly showing a second copy as free.
      const isMatchedByIndex =
        item.original_index !== undefined && item.original_index === mostProfitableFreeItem.original_index;
      const isPreviewMatch = item.original_index === undefined && mostProfitableFreeItem === item;

      if (!isMatchedByIndex && !isPreviewMatch) {
        return item;
      }

      const actualPrice = item.actual_price - item.entry.actual_price;

      return {
        ...item,
        badges: [...item.badges, t(offer.kind)],
        actual_price: actualPrice,
        offer_breakdown: {
          kind: offer.kind,
          freeQuantity: 1,
          paidQuantity: item.quantity - 1,
          unitPrice: item.entry?.actual_price,
        },
      };
    };

  return (offer, items) => ({
    apply: apply(offer, items),
    isEligible: isEligible(offer, items),
    isUnlocked: isUnlocked(offer, items),
    profit: getProfit(offer, items),
  });
};

export default useFreeItemByMinSpendValue;
